import { LmcActionKind } from "./contexts";
import { Spinner } from "../../atoms/Spinner";
import LmcVerifyEmail from "./components/LmcVerifyEmail";
import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { CustomButton } from "../../atoms/CustomButton";
import { useGetLMCUserProfile } from "../../../hooks/entity";
import { useLmcUserView } from "../../../contexts/AuthContext";
import { BiRefresh } from "react-icons/bi";
import QRCodeGenerator from "../../atoms/QRCodeGenerator";
import { sanitizeHtml } from "../../../utils/sanitizeHtml";
import { useParams } from "react-router-dom";
import {
  useGetAppleWalletPass,
  useGetGoogleWalletPassUrl,
} from "../../../hooks/wallet";

const ADD_TO_APPLE_WALLET_BADGE_URL =
  "https://cdn.1cdn.app/application/UMD/26022610561174_US-UK_Add_to_Apple_Wallet_RGB_101421.svg";
const ADD_TO_GOOGLE_WALLET_BADGE_URL =
  "https://cdn.1cdn.app/application/UMD/26022610561140_enUS_add_to_google_wallet_add-wallet-badge.svg";

const LmcDashboard = ({
  handleFlipToBack,
}: {
  handleFlipToBack: (type: "CARD_DETAIL" | "SETTINGS") => void;
}) => {
  const { t } = useTranslation("clientView");
  const { processId } = useParams<{ processId: string }>();
  const {
    state: { auth, entity },
    dispatch,
  } = useLmcUserView();

  const [userInfo, setUserInfo] = useState({
    user_email: "",
    user_identification: "",
  });

  const [isQrCodeValid, setIsQrCodeValid] = useState<boolean>(true);
  const [hasQrCodeExpiredDuringSession, setHasQrCodeExpiredDuringSession] =
    useState(false);
  const [showConfirmEmail, setShowConfirmEmail] = useState<boolean>(false);
  const [qrCodeData, setQrCodeData] = useState<string>("");
  const [logoUrl, setLogoUrl] = useState<string>("/images/logo.png");
  const [qrGeneratedAt] = useState<string>(() => moment.utc().toISOString());

  const { mutate: fetchApplePass, isLoading: isAppleLoading } =
    useGetAppleWalletPass();
  const { mutate: fetchGooglePassUrl, isLoading: isGoogleLoading } =
    useGetGoogleWalletPassUrl();

  const walletEnabled = Boolean(
    entity?.process_data?.wallet_settings?.wallet_enabled
  );

  const [platform, setPlatform] = useState<"ios" | "android" | "desktop">(
    "desktop"
  );
  useEffect(() => {
    const ua = navigator.userAgent || "";
    if (/iPad|iPhone|iPod/.test(ua)) {
      setPlatform("ios");
    } else if (/android/i.test(ua)) {
      setPlatform("android");
    }
  }, []);

  const handleAppleWallet = useCallback(() => {
    if (!processId || !auth.user_identification) return;
    if (!isQrCodeValid) {
      toast.error(t("lmcUser.card.qrExpired"));
      return;
    }
    fetchApplePass(
      { processId, userIdentification: auth.user_identification },
      {
        onSuccess: (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `loyalty-${auth.user_identification}.pkpass`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        },
        onError: (err) => {
          console.error("Apple Wallet error:", err);
          toast.error(t("card.walletError"));
        },
      }
    );
  }, [processId, auth.user_identification, fetchApplePass, isQrCodeValid, t]);

  const handleGoogleWallet = useCallback(() => {
    if (!processId || !auth.user_identification) return;
    if (!isQrCodeValid) {
      toast.error(t("lmcUser.card.qrExpired"));
      return;
    }
    const newWindow = window.open("about:blank", "_blank");
    fetchGooglePassUrl(
      { processId, userIdentification: auth.user_identification },
      {
        onSuccess: (url) => {
          if (newWindow) {
            newWindow.location.href = url;
          } else {
            const link = document.createElement("a");
            link.href = url;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
        onError: (err) => {
          console.error("Google Wallet error:", err);
          if (newWindow) newWindow.close();
          toast.error(t("card.walletError"));
        },
      }
    );
  }, [
    processId,
    auth.user_identification,
    fetchGooglePassUrl,
    isQrCodeValid,
    t,
  ]);

  const { isLoading, isError, isSuccess, data, refetch } = useGetLMCUserProfile(
    entity?.entity_data?.entity_identification as string,
    auth.user_identification as string,
    {
      onSuccess: (data) => {
        dispatch({
          type: LmcActionKind.LMCDATA,
          payload: data,
        });

        if (!data?.user?.is_active) {
          setUserInfo({
            user_email: data?.user?.email,
            user_identification: auth?.user_identification || "",
          });
        }

        setShowConfirmEmail(!data?.user?.is_active);
      },
    }
  );

  useEffect(() => {
    const qrInitializer = async () => {
      let targetSrc;

      try {
        await fetch(
          entity?.entity_data?.entity_logo ||
            "https://cdn.1cdn.app/application/UMD/2405110558455_logo.png"
        ).then((res) => {
          if (res.status === 200) {
            targetSrc =
              entity.entity_data?.entity_logo ||
              "https://cdn.1cdn.app/application/UMD/2405110558455_logo.png";
          }
        });
      } catch (error) {
        targetSrc = "/images/logo.png";
      }

      setLogoUrl(targetSrc || "/images/logo.png");
    };

    qrInitializer();
  }, [entity?.entity_data?.entity_logo]);

  useEffect(() => {
    if (isSuccess) {
      const data = isQrCodeValid
        ? JSON.stringify({
            userIdentification:
              auth.user_identification || userInfo.user_identification,
            exp: moment(qrGeneratedAt).add(5, "minutes").utc().format(),
          })
        : t("lmcUser.key3").toString();

      setQrCodeData(data);
    }
  }, [
    qrGeneratedAt,
    auth.user_identification,
    isQrCodeValid,
    isSuccess,
    t,
    userInfo.user_identification,
  ]);

  useEffect(() => {
    let to = setInterval(() => {
      if (isSuccess) {
        const isValid = moment(qrGeneratedAt)
          .add(5, "minutes")
          .utc()
          .isAfter(moment.utc());

        if (isQrCodeValid && !isValid) {
          setHasQrCodeExpiredDuringSession(true);
        }

        setIsQrCodeValid(isValid);
      }
    }, 1000);

    return () => {
      clearInterval(to);
    };
  }, [qrGeneratedAt, isSuccess, isQrCodeValid]);

  useEffect(() => {
    if (!isQrCodeValid && hasQrCodeExpiredDuringSession) {
      toast.warn(t("lmcUser.card.invalidQr"));
    }
  }, [isQrCodeValid, hasQrCodeExpiredDuringSession, t]);

  let content;

  if (isLoading)
    content = (
      <span translate="no">
        <Spinner size="sm" />
      </span>
    );

  if (isError)
    content = (
      <div className="w-full p-4 flex flex-col justify-start items-center gap-3">
        <h5 className="text-gray-200">{t("lmcUser.key4")} </h5>
        <CustomButton
          onClick={() => refetch()}
          title={t("lmcUser.key2") as string}
        />
      </div>
    );

  if (isSuccess)
    content = (
      <React.Fragment>
        <div className="w-full px-4 flex my-auto flex-col justify-start items-center">
          <div className="relative w-full flex justify-center mt-4 mb-2">
            <div
              className="w-10 h-4 rounded-full bg-gradient-to-b from-gray-300 to-gray-100 border border-gray-300"
              style={{
                boxShadow:
                  "inset 0 2px 4px rgba(0,0,0,0.3), inset 0 -1px 2px rgba(255,255,255,0.5)",
              }}
            >
              <div
                className="w-full h-full rounded-full"
                style={{
                  background:
                    "radial-gradient(ellipse at 30% 30%, rgba(255,255,255,0.4) 0%, transparent 50%)",
                }}
              />
            </div>
          </div>

          <div className="w-full px-2 flex justify-end">
            <CustomButton
              variant="none"
              size="icon"
              onClick={() => {
                dispatch({ type: LmcActionKind.LOGOUT, payload: {} });
              }}
              tooltip={String(t("lmcUser.key2"))}
              icon={<BiRefresh className="w-5 h-5" />}
              className="gap-2 p-2 rounded-full border border-gray-300 text-gray-600 hover:text-gray-800"
            />
          </div>

          <h5
            className="w-full mt-6 mb-8 text-center text-gray-800 text-lg font-bold font-figtree"
            translate="no"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                entity?.process_data?.comment?.replace(/\n/g, "<br />") ||
                  String(t("lmcUser.key39") || "")
              ),
            }}
          ></h5>

          <QRCodeGenerator
            data={qrCodeData}
            logoUrl={logoUrl}
            showLogo={true}
            // design-ok: noir pur impose par le contraste de lecture des QR codes
            fgColor="#000000"
            ecLevel="H"
            ariaLabel={`QR code for Loyalty Program`}
            className="mb-2"
          />

          {processId &&
            auth.user_identification &&
            platform !== "desktop" &&
            walletEnabled && (
              <div className="flex items-center justify-center gap-3 mt-2 mb-2">
                {platform === "ios" && (
                  <button
                    type="button"
                    onClick={handleAppleWallet}
                    disabled={isAppleLoading}
                    className="border-0 bg-transparent p-0 disabled:opacity-50"
                  >
                    <img
                      src={ADD_TO_APPLE_WALLET_BADGE_URL}
                      alt={t("card.addToAppleWallet") as string}
                      className="h-10 cursor-pointer hover:opacity-80 transition-opacity"
                    />
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleGoogleWallet}
                  disabled={isGoogleLoading}
                  className="border-0 bg-transparent p-0 disabled:opacity-50"
                >
                  <img
                    src={ADD_TO_GOOGLE_WALLET_BADGE_URL}
                    alt={t("card.addToGoogleWallet") as string}
                    className="h-10 cursor-pointer hover:opacity-80 transition-opacity"
                  />
                </button>
              </div>
            )}
        </div>
        {data.data?.lmc_type && data.data.lmc_value?.[0]?.value && (
          <div className="w-full mt-auto p-4 mb-3 flex items-center gap-4">
            {isQrCodeValid && (
              <CustomButton
                size="lg"
                onClick={() => {
                  handleFlipToBack("CARD_DETAIL");
                }}
                title={t("lmcUser.key5")}
                className="w-full text-gray-900 font-semibold"
              />
            )}

            {isQrCodeValid && (
              <CustomButton
                size="lg"
                onClick={() => {
                  handleFlipToBack("SETTINGS");
                }}
                tooltip={String(t("dashboard.profile.key1"))}
                className="text-gray-900"
                icon="settings"
              />
            )}
          </div>
        )}
      </React.Fragment>
    );

  return (
    <React.Fragment>
      {showConfirmEmail ? (
        <LmcVerifyEmail
          userEmail={userInfo.user_email}
          userId={userInfo.user_identification}
          showConfirmEmailModal={showConfirmEmail}
          onClose={() => setShowConfirmEmail(false)}
          handleLogin={() => refetch()}
          isLoading={isLoading}
        />
      ) : (
        content
      )}
    </React.Fragment>
  );
};

export default LmcDashboard;
